import React from "react";
import { motion } from "framer-motion";
import { Home, Info, Mail } from "lucide-react";
import { useLocation } from "react-router-dom";

// Animation variants for each menu item
const itemVariants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  closed: {
    y: 20,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
      duration: 0.2,
    },
  },
};

// Icon mapping by route
const iconMap = {
  "/": Home,
  "/about": Info,
  "/contact": Mail,
};

export const MenuItem = ({ item, onClose }) => {
  const { text, onClick, route } = item;
  const location = useLocation();

  // Determine if this is the current route
  const isActive = location.pathname === route;

  // Get the appropriate icon
  const Icon = iconMap[route] || Home;

  const handleClick = () => {
    // Close sidebar first to prevent race condition with navigation
    if (onClose) onClose();
    // Small delay to ensure close animation starts before navigation
    setTimeout(() => {
      onClick();
    }, 50);
  };

  return (
    <motion.li
      variants={itemVariants}
      className="list-none"
    >
      <motion.button
        onClick={handleClick}
        className={`
          relative w-full
          flex items-center gap-3
          px-4 py-3
          rounded-lg
          text-left
          transition-all duration-200
          cursor-pointer
          outline-none
          ${isActive
            ? "bg-amber-100 text-amber-800 font-medium"
            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
          }
        `}
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Active indicator */}
        {isActive && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-amber-500 rounded-r-full"
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}

        {/* Icon */}
        <div className={`
          flex items-center justify-center
          w-9 h-9
          rounded-lg
          transition-colors duration-200
          ${isActive
            ? "bg-amber-200/50 text-amber-700"
            : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
          }
        `}>
          <Icon className="w-[18px] h-[18px]" />
        </div>

        {/* Text */}
        <span className="font-medium text-[15px]">
          {text}
        </span>
      </motion.button>
    </motion.li>
  );
};

export default MenuItem;
